import { differenceInHours } from 'date-fns';

import { Link } from '@mui/material';
import Button from '@mui/material/Button';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import ListItemText from '@mui/material/ListItemText';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useBoolean } from 'src/hooks/use-boolean';

import { fDateTime } from 'src/utils/format-time';
import { hasPermission } from 'src/utils/has-permissions';
import { fCurrencyRupees } from 'src/utils/format-number';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';

import Label from 'src/components/label';

import { IChequeItem } from 'src/types/cheque';

import MarkSettledDialog from './mark-settled-dialog';

type Props = {
  row: IChequeItem;
  onSettled: VoidFunction;
};

export default function OverdueTableRow({ row, onSettled }: Props) {
  const { _id, chequeNumber, bank, payerName, payerAccountNumber, amountInCents } = row;

  const { user: authUser } = useAuthContext();
  const showSettledDialog = useBoolean();

  const hoursOverdue = row.factoringSettlementDueAt
    ? differenceInHours(new Date(), new Date(row.factoringSettlementDueAt))
    : 0;

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
          <ListItemText
            primary={bank}
            secondary={chequeNumber}
            primaryTypographyProps={{ typography: 'body2' }}
            secondaryTypographyProps={{
              component: 'span',
              color: 'text.disabled',
              typography: 'caption',
            }}
          />
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
          <ListItemText
            primary={payerName}
            secondary={payerAccountNumber}
            primaryTypographyProps={{ typography: 'body2' }}
            secondaryTypographyProps={{
              component: 'span',
              color: 'text.disabled',
              typography: 'caption',
            }}
          />
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
          {fCurrencyRupees(amountInCents)}
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
          {fDateTime(row.factoringSettlementDueAt)}
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
          <Label variant="soft" color="error">
            Overdue by {hoursOverdue}h
          </Label>
        </TableCell>

        <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap' }}>
          <Link component={RouterLink} href={paths.dashboard.cheque.view(_id)} mr={2}>
            <Button variant="soft" color="primary">
              View
            </Button>
          </Link>

          {hasPermission(
            authUser,
            'any',
            AuthPermissions.UPDATE_FACTORING_CHEQUE_STATUS,
            AuthPermissions.ADMIN_PERMISSION,
            AuthPermissions.SUPER_ADMIN_PERMISSION
          ) && (
            <Button
              variant="contained"
              color="warning"
              onClick={() => showSettledDialog.onTrue()}
            >
              Mark Settled
            </Button>
          )}
        </TableCell>
      </TableRow>

      {showSettledDialog.value && (
        <MarkSettledDialog
          cheque={row}
          isOpen={showSettledDialog.value}
          onClose={() => showSettledDialog.onFalse()}
          onSuccess={onSettled}
        />
      )}
    </>
  );
}
